import { useMutation } from "@tanstack/react-query";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";

import { StoryPlayback } from "@baiki/interfaces";

import { db } from "@/lib/firebase";
import { useAuth } from "@/providers";

/**
 * Hook to track story playbacks
 * Call this when a user starts playing a story
 */
export const useTrackPlayback = () => {
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      storyId,
      source,
    }: {
      storyId: string;
      source?: StoryPlayback["source"];
    }) => {
      if (!db) {
        console.warn("[Analytics] Firestore not initialized");
        return null;
      }

      // Create playback event
      const playback: Omit<StoryPlayback, "id"> = {
        storyId,
        userId: user?.uid, // Optional - tracks who (or undefined for anonymous)
        timestamp: new Date(),
        source: source || "library",
        // completed and duration can be updated later when playback ends
      };

      console.log("[Analytics] Tracking playback:", { storyId, source });

      const docRef = await addDoc(collection(db, "analytics"), {
        ...playback,
        timestamp: serverTimestamp(),
      });

      return docRef.id;
    },
  });
};

/**
 * Hook to update playback completion
 * Call this when a user finishes or stops playing
 */
export const useUpdatePlayback = () => {
  return useMutation({
    mutationFn: async ({
      playbackId,
      completed,
      duration,
    }: {
      playbackId: string;
      completed: boolean;
      duration: number;
    }) => {
      if (!db) {
        console.warn("[Analytics] Firestore not initialized");
        return;
      }

      // TODO: Update playback document with completion data
      console.log("[Analytics] Update playback:", {
        playbackId,
        completed,
        duration,
      });

      // Uncomment when ready to implement:
      // const docRef = doc(db, "analytics", playbackId);
      // await updateDoc(docRef, { completed, duration });
    },
  });
};

export const AnalyticsQueries = {
  useTrackPlayback,
  useUpdatePlayback,
};
